<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {Check, ChevronRight, CircleAlert, LoaderCircle} from "@lucide/vue";
import {ScanProgress, ScanStep} from "@/medication-checkup/interfaces";
import {CARD_SHEET} from "@/medication-checkup/services/SceneClasses";

/**
 * The scan card: progress for a scan the server is running, stage by stage,
 * and what it leaves in the thread once done — the card stays, checked and
 * the bar full, while the scene continues below it.
 *
 * This component owns the rendering only: the badge, a progress bar paced on
 * the counts the stream announced, an elapsed-seconds chip, one section per
 * stage — the dispensing history, then the reading of the prescriptions —
 * and a retry button when the scan fails rather than leaving the thread
 * hanging.
 */

const props = defineProps<{
    failed?: boolean;
    /**
     * Whether the scan has answered: the card settles — bar full, seconds
     * frozen, badges checked — and stays in the thread as a record of the work.
     */
    done?: boolean;
    progress?: ScanProgress;
    /**
     * Incremented by the scene on every new run, to restart the counter.
     */
    run?: number;
    /**
     * Whose record the scan reads, served under the title.
     */
    subject?: string | null;
    /**
     * What the card says the server is doing — the collection of the record
     * by default, the analysis of the treatments when the scene runs one.
     */
    title?: string;
    retryLabel?: string;
}>();

const emit = defineEmits<{retry: []}>();

const started = ref(false);

/**
 * How long the scan has been running, shown as a chip so the wait reads as
 * work under way rather than as a frozen screen.
 */
const seconds = ref(0);

let ticker: number | undefined;

onMounted(() => {
    window.requestAnimationFrame(() => started.value = true);

    ticker = window.setInterval(() => {
        if (!props.failed && !props.done) {
            seconds.value += 1;
        }
    }, 1000);
});

onUnmounted(() => window.clearInterval(ticker));

/**
 * A retry reuses this card rather than remounting it, so the elapsed counter
 * restarts on each new run — otherwise the new scan would inherit the seconds
 * of the one that failed.
 */
watch(() => props.run, () => seconds.value = 0);

const steps = computed(() => props.progress?.steps ?? []);

/**
 * Whether the stream has announced a stage yet. Until it does, the bar has
 * no count to pace itself against.
 */
const announced = computed(() => steps.value.length > 0);

/**
 * Whether the bar can be paced on counts: the stage under way announced how
 * many lines it will tell. A stage that cannot know — the steps of an agent —
 * leaves the bar to its creep rather than full at its first line.
 */
const counted = computed(() => announced.value && steps.value.at(-1)?.total !== null);

/**
 * Whether a stage is over: every stage but the last one is, once the next
 * opened; the last one is when the scan answered, or when it failed.
 */
const settled = (step: ScanStep, index: number): boolean => {
    return step.failed !== null || props.done === true || index < steps.value.length - 1;
};

/**
 * The width of the progress bar: full once the scan answered, real progress
 * against the announced counts while the stream reports, and a slow creep
 * otherwise so the bar never sits still while work is happening.
 */
const width = computed(() => {
    if (props.failed) {
        return '6%';
    }

    if (props.done) {
        return '100%';
    }

    if (counted.value) {
        const total = Math.max(steps.value.reduce((sum, step) => sum + (step.total ?? step.lines.length), 0), 1);
        const reported = steps.value.reduce((sum, step) => sum + step.lines.length, 0);

        return `${Math.max(6, Math.round((reported / total) * 100))}%`;
    }

    return started.value ? '92%' : '6%';
});

/**
 * The creep animates slowly; real progress snaps quickly.
 */
const widthBeat = computed(() => counted.value ? '400ms' : '2000ms');
</script>

<template>
  <div
    :class="CARD_SHEET"
    class="max-w-[640px] rounded-2xl px-5 py-[18px]"
  >
    <div class="flex items-center gap-3">
      <span
        class="w-10 h-10 shrink-0 rounded-full flex items-center justify-center
               bg-gradient-to-r from-primary-dark to-primary"
      >
        <img src="@/assets/images/logos/apodis-white.svg" alt="" class="h-5">
      </span>

      <div class="flex-1 min-w-0">
        <div class="font-['Comfortaa'] text-lg font-bold text-primary">
          {{ title ?? "L'IA Apodis récupère le dossier" }}
        </div>
        <div v-if="subject" class="text-sm text-muted-foreground">
          {{ subject }}
        </div>
      </div>

      <span
        class="shrink-0 border border-slate-200 rounded-full px-3 py-0.5
               text-sm font-semibold text-primary"
      >
        {{ seconds }} s
      </span>
    </div>

    <div class="h-1.5 rounded-full bg-gray-light mt-3 mb-4 overflow-hidden">
      <div
        class="h-full rounded-full bg-primary transition-[width] ease-out"
        :style="{width, transitionDuration: widthBeat}"
      />
    </div>

    <div v-if="announced" class="flex flex-col gap-3">
      <div
        v-for="(step, index) in steps"
        :key="index"
        class="flex items-start gap-3 animate-message-in"
        :class="{'pt-3 border-t border-gray-light': index > 0}"
      >
        <span
          class="w-[34px] h-[34px] shrink-0 rounded-full flex items-center justify-center"
          :class="step.failed !== null
            ? 'bg-red-50 text-red-500'
            : settled(step, index) ? 'bg-secondary text-primary-dark' : 'bg-primary text-white'"
        >
          <CircleAlert v-if="step.failed !== null" class="w-[17px] h-[17px]" />
          <Check v-else-if="settled(step, index)" class="w-[17px] h-[17px]" />
          <LoaderCircle v-else class="w-[17px] h-[17px] animate-spin" />
        </span>

        <div class="flex-1 min-w-0 pt-[7px]">
          <div class="text-sm font-bold">
            {{ step.title }}
          </div>

          <div v-if="step.lines.length > 0 || step.failed !== null" class="flex flex-col gap-1 mt-1.5">
            <div
              v-for="(line, lineIndex) in step.lines"
              :key="lineIndex"
              class="flex items-start gap-1.5 text-sm leading-normal animate-message-in"
            >
              <ChevronRight class="w-3.5 h-3.5 mt-1 shrink-0 text-secondary-darkest" />
              {{ line }}
            </div>
            <div
              v-if="step.failed !== null"
              class="flex items-start gap-1.5 text-sm leading-normal text-red-500 animate-message-in"
            >
              <ChevronRight class="w-3.5 h-3.5 mt-1 shrink-0" />
              {{ step.failed }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="failed" class="text-center mt-3.5">
      <button
        class="text-sm font-semibold text-primary hover:underline"
        @click="emit('retry')"
      >
        {{ retryLabel ?? 'La récupération a échoué — réessayer' }}
      </button>
    </div>
  </div>
</template>
